import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type ExportFormat,
  type TestCaseView,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { SelectField } from '../components/SelectField';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { isApiError } from '../api/errors';
import { AuthedBrand } from './AuthedBrand';
import { journeyTypeLabel } from './journeyLabels';
import styles from './TestCases.module.css';

type LoadState = 'loading' | 'error' | 'ready';

export interface TestCasesProps {
  client?: ApiClient;
}

const EXPORT_OPTIONS: { value: ExportFormat; label: string; ext: string }[] = [
  { value: 'gherkin', label: 'Gherkin (.feature)', ext: 'feature' },
  { value: 'playwright', label: 'Playwright (.spec.ts)', ext: 'spec.ts' },
  { value: 'selenium', label: 'Selenium (.selenium.js)', ext: 'selenium.js' },
  { value: 'cypress', label: 'Cypress (.cy.ts)', ext: 'cy.ts' },
  { value: 'pytest', label: 'PyTest (.py)', ext: 'py' },
  { value: 'testng', label: 'TestNG (.java)', ext: 'java' },
];

function formatTime(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/** The short version anchor shown under each case: which generator + ruleset +
 * materializer + step-content produced this exact artifact. */
function versionAnchor(tc: TestCaseView): string {
  const parts = [tc.generatorVersion];
  if (tc.rulesetVersion) parts.push(`ruleset ${tc.rulesetVersion}`);
  if (tc.materializerVersion) parts.push(`materializer ${tc.materializerVersion}`);
  parts.push(`steps ${tc.stepsHash.slice(0, 8)}`);
  return parts.join(' · ');
}

interface TestCaseItemProps {
  tc: TestCaseView;
  orgId: string;
  projectId: string;
  client: ApiClient;
  onAnnounce: (message: string) => void;
}

/** One test case: the current Gherkin version plus export and authoring controls
 * (#165) — edit into a new version, disable/enable the chain, and view the version
 * history. Exported code is a generated, throwaway artifact — copy or download. */
function TestCaseItem({ tc, orgId, projectId, client, onAnnounce }: TestCaseItemProps) {
  const [current, setCurrent] = useState<TestCaseView>(tc);
  // Re-sync if the parent refetches and hands down a newer snapshot for this chain.
  useEffect(() => setCurrent(tc), [tc]);

  const label = journeyTypeLabel(current.journeyType);
  const caseId = current.id;
  const [format, setFormat] = useState<ExportFormat>('gherkin');
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<{ format: ExportFormat; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Authoring state (#165).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [versions, setVersions] = useState<TestCaseView[] | null>(null);
  const [showVersions, setShowVersions] = useState(false);

  // Restore this user's remembered format for this version (#166). On load failure
  // we keep the Gherkin default. Guarded against a late resolve after id change.
  useEffect(() => {
    let active = true;
    client
      .getTestCaseFormatPreference(orgId, projectId, caseId)
      .then((pref) => {
        if (active) setFormat(pref.format);
      })
      .catch(() => {
        /* No stored preference reachable — leave the default in place. */
      });
    return () => {
      active = false;
    };
  }, [client, orgId, projectId, caseId]);

  const changeFormat = useCallback(
    (next: ExportFormat) => {
      setFormat(next);
      void client.setTestCaseFormatPreference(orgId, projectId, caseId, next).catch(() => {
        onAnnounce('Could not save your export-format preference.');
      });
    },
    [client, orgId, projectId, caseId, onAnnounce],
  );

  const runExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const text = await client.exportTestCase(orgId, projectId, caseId, format);
      setExported({ format, text });
      onAnnounce(`Exported ${label} as ${format}.`);
    } catch (err) {
      setError(
        isApiError(err) && err.status === 409
          ? 'This test case is out of date — regenerate it before exporting.'
          : toDisplayError(err).message,
      );
    } finally {
      setExporting(false);
    }
  }, [client, orgId, projectId, caseId, format, exporting, label, onAnnounce]);

  const copy = useCallback(async () => {
    if (!exported) return;
    await navigator.clipboard.writeText(exported.text);
    onAnnounce('Copied to clipboard.');
  }, [exported, onAnnounce]);

  const download = useCallback(() => {
    if (!exported) return;
    const ext = EXPORT_OPTIONS.find((o) => o.value === exported.format)?.ext ?? 'txt';
    const blob = new Blob([exported.text], { type: 'text/plain' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `${current.journeyType}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }, [exported, current.journeyType]);

  const startEdit = useCallback(() => {
    setDraft(current.renderedText);
    setError(null);
    setEditing(true);
  }, [current.renderedText]);

  const saveEdit = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const next = await client.editTestCase(orgId, projectId, caseId, draft);
      setCurrent(next);
      setEditing(false);
      // The chain changed — drop any cached version list.
      setVersions(null);
      setShowVersions(false);
      onAnnounce(`Saved ${label} as revision ${next.revisionNumber}.`);
    } catch (err) {
      setError(toDisplayError(err).message);
    } finally {
      setSaving(false);
    }
  }, [client, orgId, projectId, caseId, draft, saving, label, onAnnounce]);

  const toggleStatus = useCallback(async () => {
    if (statusBusy) return;
    const next = current.status === 'active' ? 'disabled' : 'active';
    setStatusBusy(true);
    setError(null);
    try {
      const res = await client.setTestCaseStatus(orgId, projectId, caseId, next);
      setCurrent((c) => ({ ...c, status: res.status }));
      onAnnounce(`${label} ${res.status === 'disabled' ? 'disabled' : 'enabled'}.`);
    } catch (err) {
      setError(toDisplayError(err).message);
    } finally {
      setStatusBusy(false);
    }
  }, [client, orgId, projectId, caseId, current.status, statusBusy, label, onAnnounce]);

  const toggleVersions = useCallback(async () => {
    if (showVersions) {
      setShowVersions(false);
      return;
    }
    try {
      const vs = versions ?? (await client.listTestCaseVersions(orgId, projectId, caseId));
      setVersions(vs);
      setShowVersions(true);
    } catch (err) {
      setError(toDisplayError(err).message);
    }
  }, [client, orgId, projectId, caseId, versions, showVersions]);

  const disabled = current.status === 'disabled';

  return (
    <li className={disabled ? `${styles.item} ${styles.disabledItem}` : styles.item}>
      <div className={styles.itemHeader}>
        <h2 className={styles.itemHeading}>{label}</h2>
        <span className={styles.mutedBadge}>{current.format}</span>
        <span className={styles.mutedBadge}>
          v{current.revisionNumber}
          {current.source === 'authored' ? ' · edited' : ''}
        </span>
        {disabled ? <span className={styles.mutedBadge}>Disabled</span> : null}
      </div>
      <p className={styles.meta}>
        <span>{versionAnchor(current)}</span>
        {formatTime(current.createdAt) ? <span>{formatTime(current.createdAt)}</span> : null}
      </p>
      <pre className={styles.gherkin} aria-label={`Gherkin for ${label}`}>
        {current.renderedText}
      </pre>

      <div className={styles.authorRow}>
        <Button variant="secondary" onClick={startEdit}>
          Edit
        </Button>
        <Button variant="secondary" busy={statusBusy} onClick={() => void toggleStatus()}>
          {disabled ? 'Enable' : 'Disable'}
        </Button>
        <Button variant="secondary" onClick={() => void toggleVersions()}>
          {showVersions ? 'Hide versions' : `Versions (${current.revisionNumber})`}
        </Button>
      </div>

      {editing ? (
        <div className={styles.editArea}>
          <textarea
            className={styles.editTextarea}
            aria-label={`Edit Gherkin for ${label}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className={styles.authorRow}>
            <Button variant="primary" busy={saving} onClick={() => void saveEdit()}>
              {saving ? 'Saving…' : 'Save as new version'}
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {showVersions && versions ? (
        <ul className={styles.versionList} aria-label={`Versions of ${label}`}>
          {versions.map((v) => (
            <li key={v.id} className={styles.versionItem}>
              <span>v{v.revisionNumber}</span>
              <span>{v.source === 'authored' ? 'edited' : 'generated'}</span>
              {formatTime(v.createdAt) ? <span>{formatTime(v.createdAt)}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      <div className={styles.exportRow}>
        <SelectField
          label="Export format"
          id={`export-format-${caseId}`}
          value={format}
          onChange={(v) => changeFormat(v as ExportFormat)}
          options={EXPORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        <Button variant="secondary" busy={exporting} onClick={runExport}>
          {exporting ? 'Exporting…' : 'Export'}
        </Button>
      </div>

      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

      {exported ? (
        <div className={styles.exportResult}>
          <div className={styles.exportActions}>
            <span className={styles.mutedBadge}>{exported.format}</span>
            <Button variant="secondary" onClick={copy}>
              Copy
            </Button>
            <Button variant="secondary" onClick={download}>
              Download
            </Button>
          </div>
          <pre className={styles.gherkin} aria-label={`${exported.format} export for ${label}`}>
            {exported.text}
          </pre>
        </div>
      ) : null}
    </li>
  );
}

/**
 * Project test-cases view (#35, #165). Lists the current version of each test-case
 * chain for a project, rendered verbatim with the version anchor that records which
 * logic + step content produced it. Each case can be exported (#36) to Gherkin /
 * Playwright / Selenium / Cypress, and AUTHORED (#165): edit the Gherkin into a new
 * version (append-only — prior versions are retained and viewable), and disable /
 * enable the chain. A manual Refresh re-fetches (no polling); async results announce
 * in a polite live region.
 */
export function TestCases({ client = defaultClient }: TestCasesProps) {
  const { currentOrg } = useOrg();
  const { id } = useParams<{ id: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [cases, setCases] = useState<TestCaseView[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !id) return;
    setPhase('loading');
    try {
      const next = await client.listTestCases(orgId, id);
      setCases(next);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    if (!orgId || !id || refreshing) return;
    setRefreshing(true);
    try {
      const next = await client.listTestCases(orgId, id);
      setCases(next);
      setStatusMessage('Test cases updated.');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    } finally {
      setRefreshing(false);
    }
  }, [client, orgId, id, refreshing]);

  // #422 — generate (idempotently) a deterministic test case for every journey in
  // the project. Best-effort per journey; reports a created/up-to-date/failed tally.
  const onGenerateAll = useCallback(async () => {
    if (!orgId || !id || generating) return;
    setGenerating(true);
    setGenerateMessage(null);
    try {
      const health = await client.journeyHealth(orgId, id);
      const journeyIds = Array.from(
        new Set(
          health.journeys
            .map((j) => j.journeyId)
            .filter((jid): jid is string => Boolean(jid)),
        ),
      );
      if (journeyIds.length === 0) {
        setGenerateMessage('No journeys to generate from yet — run a scan to discover journeys first.');
        return;
      }
      const results = await Promise.allSettled(
        journeyIds.map((jid) => client.generateTestCase(orgId, id, jid)),
      );
      const created = results.filter((r) => r.status === 'fulfilled' && r.value.created).length;
      const existing = results.filter((r) => r.status === 'fulfilled' && !r.value.created).length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      const parts = [`${created} generated`, `${existing} already up to date`];
      if (failed > 0) parts.push(`${failed} failed`);
      setGenerateMessage(`Test case generation complete: ${parts.join(', ')}.`);
      setCases(await client.listTestCases(orgId, id));
    } catch (err) {
      setGenerateMessage(toDisplayError(err).message);
    } finally {
      setGenerating(false);
    }
  }, [client, orgId, id, generating]);

  const backHref = `/projects/${id ?? ''}/journey-health`;
  const canWrite = currentOrg?.role === 'org_admin' || currentOrg?.role === 'editor';

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <p className={styles.back}>
          <Link to={backHref}>&larr; Back to journey health</Link>
        </p>
        <h1 id="tc-heading" className={styles.heading}>
          Test cases
        </h1>
        <p className={styles.subheading}>
          Gherkin test cases generated from this project&rsquo;s customer journeys. Each is a
          read-only snapshot stamped with the version that produced it, and can be exported to
          Gherkin or Playwright as a generated, throwaway artifact.
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading test cases…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <>
            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            <div className={styles.actions}>
              <Button variant="secondary" busy={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
              {canWrite ? (
                <Button variant="primary" busy={generating} onClick={() => void onGenerateAll()}>
                  {generating ? 'Generating…' : 'Generate test cases'}
                </Button>
              ) : null}
            </div>

            {generateMessage ? (
              <div className={styles.panel}>
                <InlineAlert severity="info">{generateMessage}</InlineAlert>
              </div>
            ) : null}

            {cases.length === 0 ? (
              <div className={styles.panel}>
                <InlineAlert severity="info">
                  No test cases have been generated for this project yet.
                </InlineAlert>
              </div>
            ) : (
              <ul className={styles.list}>
                {cases.map((tc) => (
                  <TestCaseItem
                    key={tc.id}
                    tc={tc}
                    orgId={orgId ?? ''}
                    projectId={id ?? ''}
                    client={client}
                    onAnnounce={setStatusMessage}
                  />
                ))}
              </ul>
            )}
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
